import {useId} from '@mantine/hooks';
import React from 'react';

import {Side} from 'constants/MolbioConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ControlledNumericInput from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {Optional} from 'util/Types';

export interface SelectionEndpointInputProps {
  displayValue: Optional<number>;
  setFromDisplay: (value: number | null) => void;
  side: Side;
  displayCutsites: Array<number> | null;
  isDisabled?: boolean;
}

function TextEndpointInput({
  displayValue,
  setFromDisplay,
  isDisabled,
  id,
}: Omit<SelectionEndpointInputProps, 'side' | 'displayCutsites'> & {id: string}): JSX.Element {
  return (
    <ControlledNumericInput
      id={id}
      value={displayValue}
      onChange={(e) => {
        setFromDisplay(e.target.numericValue);
      }}
      isDisabled={isDisabled}
    />
  );
}

function DropdownEndpointInput({
  displayValue,
  setFromDisplay,
  displayCutsites,
  isDisabled,
  id,
}: SelectionEndpointInputProps & {
  displayCutsites: Array<number>;
  id: string;
}): JSX.Element {
  let menuOptions: Array<JSX.Element>;
  if (displayCutsites.length === 0) {
    menuOptions = [
      <Option key="no-cut-sites" isDisabled={true}>
        No cut sites detected
      </Option>,
    ];
  } else {
    menuOptions = displayCutsites.map((displayCutsite) => (
      <Option
        key={displayCutsite}
        value={displayCutsite}
        onSelect={setFromDisplay}
        dataTestAttrs={{
          component: 'DropdownEndpointInput',
          element: 'dropdownOption',
          key: displayCutsite.toString(),
        }}
      >
        {displayCutsite}
      </Option>
    ));
  }
  return (
    <SelectDropdown
      displayName={displayValue ?? ''}
      id={id}
      menu={
        <Menu scrollable={true} width={MenuWidth.FIT_CONTENT}>
          {menuOptions}
        </Menu>
      }
      disabled={isDisabled}
    />
  );
}

export default function SelectionEndpointInput({
  displayValue,
  setFromDisplay,
  side,
  displayCutsites,
  isDisabled,
}: SelectionEndpointInputProps): JSX.Element {
  const inputId = useId();
  return (
    <div {...getTestProps({component: 'FragmentCardContents', element: 'selectionEndpointInput', key: side})}>
      <FieldLabel htmlFor={inputId} label={side === 'start' ? 'Start' : 'End'} />
      {displayCutsites == null ? (
        <TextEndpointInput
          displayValue={displayValue}
          setFromDisplay={setFromDisplay}
          isDisabled={isDisabled}
          id={inputId}
        />
      ) : (
        <DropdownEndpointInput
          displayValue={displayValue}
          setFromDisplay={setFromDisplay}
          displayCutsites={displayCutsites}
          isDisabled={isDisabled}
          id={inputId}
          side={side}
        />
      )}
    </div>
  );
}
